import React from 'react';
import { motion } from 'framer-motion';
import { FaEnvelope, FaExternalLinkAlt, FaGraduationCap, FaCode, FaUsers, FaLightbulb } from 'react-icons/fa';
import resumePDF from '../assets/Shalender-resume-2025.pdf'; 

const About = () => {
  const highlights = [
    {
      icon: FaCode,
      title: "Technical Expertise",
      description: "Specialized in PHP, Laravel, WordPress, and modern web development frameworks"
    },
    {
      icon: FaUsers,
      title: "Team Leadership",
      description: "Experience in team handling, task distribution, and client communication"
    },
    {
      icon: FaLightbulb,
      title: "Problem Solving",
      description: "Proven track record in identifying and solving complex technical issues"
    }
  ];

  return (
    <section id="about" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            About Me
          </h2>
          <div className="w-20 h-1 bg-blue-600 mx-auto"></div>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Image Section */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="relative z-10">
              <img
                src="https://images.unsplash.com/photo-1626362814904-d27009a10da7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwyfHxkZXZlbG9wZXIlMjBjb2Rpbmd8ZW58MHx8fHwxNzU0MTM4MzEwfDA&ixlib=rb-4.1.0&q=85"
                alt="Coding workspace"
                className="rounded-lg shadow-2xl w-full h-80 object-cover"
              />
            </div>
            <div className="absolute -bottom-6 -right-6 w-full h-full bg-blue-600 rounded-lg z-0"></div>
          </motion.div>

          {/* Content Section */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="space-y-6"
          >
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Passionate Web Developer with 10+ Years of Experience
              </h3>
              <p className="text-gray-600 text-lg leading-relaxed mb-6">
                My career objective is to become a successful professional in a highly competitive 
                technological world where performance is rewarded with new challenging responsibilities 
                and to serve a reputed growth-oriented industry in the field of Development and Maintenance.
              </p>
              <p className="text-gray-600 text-lg leading-relaxed">
                I have created complex applications including content management systems and e-commerce platforms. 
                My projects are based on PHP in conjunction with Core PHP, Laravel, CodeIgniter, WordPress, MySQL, 
                JavaScript, jQuery, AJAX, HTML, and CSS.
              </p>
            </div>

            {/* Education */}
            <div className="bg-white p-6 rounded-lg shadow-lg">
              <div className="flex items-center mb-3">
                <FaGraduationCap className="text-blue-600 text-xl mr-3" />
                <h4 className="text-lg font-semibold text-gray-900">Education</h4>
              </div>
              <div>
                <p className="text-gray-800 font-medium">Bachelor of Technology (B.Tech)</p>
                <p className="text-gray-600">Punjab Technical University, Punjab</p>
                <p className="text-gray-500 text-sm">2008 - 2012</p>
              </div>
            </div>

            {/* Contact Info */}
            <div className="bg-white p-6 rounded-lg shadow-lg">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center space-x-4">

                 <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <FaEnvelope className="text-blue-600 text-xl" />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900">
                      Email
                    </h4>
                    
                      <a
                        href="mailto:shalender8928@yahoo.com"
                        className="text-blue-600 hover:text-blue-700 transition-colors"
                      >
                        shalender8928@yahoo.com
                      </a>
                    
                  </div>
                </div>

                <div className="flex items-center space-x-4">

                 <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <FaExternalLinkAlt className="text-blue-600 text-xl" />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900">
                      Check my resume here
                    </h4>
                    
                      <a
                        href={resumePDF} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-700 transition-colors"
                      >
                        Resume
                      </a>
                    
                  </div>
                </div>

              </div>
            </div>
          </motion.div>
        </div>

        {/* Highlights */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true }}
          className="grid md:grid-cols-3 gap-8 mt-16"
        >
          {highlights.map((highlight, index) => (
            <div
              key={index}
              className="bg-white p-8 rounded-xl shadow-lg text-center hover:shadow-xl transition-shadow duration-300"
            >
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <highlight.icon className="text-blue-600 text-2xl" />
              </div>
              <h4 className="text-xl font-semibold text-gray-900 mb-4">
                {highlight.title}
              </h4>
              <p className="text-gray-600 leading-relaxed">
                {highlight.description}
              </p>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default About;